import React, { useEffect, useState } from "react";
import { Building2, CheckCircle2, AlertTriangle, Loader2, ExternalLink, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "../../hooks/useApi";
import { UrssafConnectLogo } from "./UrssafBrandingKit";
import { UrssafRevokeMandatDialog } from "./UrssafRevokeMandatDialog";

/**
 * Section "Mon espace Urssaf" affichée dans les Paramètres.
 * - Collecte / modifie SIRET + date_naissance + périodicité
 * - Affiche le statut du mandat TDAE (badge vert/rouge)
 * - Détecte les changements de SIRET → propose la re-connexion
 * - Affiche le statut du mandat SEPA (via API 075) + redirect Urssaf si absent
 */
export const UrssafSection = ({ profile, setProfile }) => {
  const api = useApi();
  const [status, setStatus] = useState(null); // {env, mandat, siret_configure, siret}
  const [sepa, setSepa] = useState(null); // {mandats: []}
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [siretDirty, setSiretDirty] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [connectError, setConnectError] = useState("");

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStatus = async () => {
    try {
      const r = await api.get("/urssaf/status");
      setStatus(r.data);
      // Charge la liste des mandats SEPA en parallèle (silencieux si mandat TDAE absent)
      try {
        const s = await api.get("/urssaf/sepa");
        setSepa(s.data);
      } catch {
        setSepa({ mandats: [] });
      }
    } catch (e) {
      // silently fail
    }
    setLoading(false);
  };

  const currentSiret = (profile?.siret || "").replace(/\s/g, "");
  const savedSiret = (status?.siret || "").replace(/\s/g, "");
  const siretChanged = currentSiret && savedSiret && currentSiret !== savedSiret;
  const mandatActif = status?.mandat && status.mandat.statut === "actif" && !siretChanged;

  const handleConnect = async () => {
    if (!profile?.siret || profile.siret.replace(/\s/g, "").length !== 14) {
      return toast.error("SIRET obligatoire (14 chiffres)");
    }
    if (!profile?.date_naissance) {
      return toast.error("Date de naissance obligatoire pour créer le mandat Urssaf");
    }
    setConnecting(true);
    setConnectError("");
    try {
      const r = await api.post("/urssaf/mandat", {
        siret: profile.siret.replace(/\s/g, ""),
        date_naissance: profile.date_naissance,
        periodicite: (profile?.declaration_frequence === "trimestriel") ? "T" : "M",
        accepte_mandat: true,
      });
      if (r.data?.ok) {
        toast.success("Compte Urssaf connecté ! Tu vas recevoir un email de confirmation.");
        await loadStatus();
        setConnecting(false);
        return true;
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : d?.message;
      const status = e.response?.status;
      const text = status >= 500
        ? "Service Urssaf temporairement indisponible. Ton compte n'a pas été connecté, réessaie dans quelques minutes."
        : (msg || "Impossible de créer le mandat");
      setConnectError(text);
      toast.error(text);
    }
    setConnecting(false);
    return false;
  };

  return (
    <div className="bg-card rounded-xl border p-6 space-y-5" data-testid="urssaf-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold flex items-center gap-2" style={{color: "#897FCB"}}>
          <Building2 size={20} /> Mon espace Urssaf
          <UrssafConnectLogo size={36} className="ml-1" />
        </h2>

        {!loading && (
          mandatActif ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="urssaf-badge-active">
              <CheckCircle2 size={14} /> Connecté à l'Urssaf
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid="urssaf-badge-inactive">
              <AlertTriangle size={14} /> {siretChanged ? "SIRET modifié — reconnecte" : "Non connecté"}
            </span>
          )
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        Renseigne ton SIRET et ta date de naissance pour activer la déclaration Urssaf en 1 clic depuis le dashboard.
      </p>

      {/* Alerte changement SIRET */}
      {siretChanged && (
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Ton SIRET a changé.</div>
            <div className="text-xs mt-0.5">Le mandat lié à l'ancien SIRET est caduc. Enregistre ton profil puis clique sur "Reconnecter à l'Urssaf".</div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium mb-1.5 block">SIRET</label>
          <input
            data-testid="urssaf-input-siret"
            type="text"
            inputMode="numeric"
            maxLength={17}
            placeholder="12345678901234"
            value={profile?.siret || ""}
            onChange={(e) => { setProfile({...profile, siret: e.target.value.replace(/[^\d\s]/g, "")}); setSiretDirty(true); }}
            className="w-full h-11 px-3 rounded-lg border bg-background"
          />
          <p className="text-xs text-muted-foreground mt-1">14 chiffres — dispo sur ton attestation Urssaf</p>
        </div>

        <div>
          <label className="text-sm font-medium mb-1.5 block">Date de naissance</label>
          <input
            data-testid="urssaf-input-dob"
            type="date"
            value={profile?.date_naissance || ""}
            onChange={(e) => setProfile({...profile, date_naissance: e.target.value})}
            className="w-full h-11 px-3 rounded-lg border bg-background"
          />
          <p className="text-xs text-muted-foreground mt-1">Requise par l'Urssaf pour créer le mandat</p>
        </div>
      </div>

      <div>
        <label className="text-sm font-medium mb-1.5 block">Fréquence de déclaration</label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            data-testid="urssaf-freq-mensuel"
            onClick={() => setProfile({...profile, declaration_frequence: "mensuel"})}
            className={`p-3 rounded-lg border-2 text-sm font-medium ${profile?.declaration_frequence === "mensuel" ? "border-primary bg-primary/5" : "border-input"}`}
          >Mensuelle</button>
          <button
            type="button"
            data-testid="urssaf-freq-trimestriel"
            onClick={() => setProfile({...profile, declaration_frequence: "trimestriel"})}
            className={`p-3 rounded-lg border-2 text-sm font-medium ${profile?.declaration_frequence === "trimestriel" ? "border-primary bg-primary/5" : "border-input"}`}
          >Trimestrielle</button>
        </div>
      </div>

      {/* Sous-section : Mandat SEPA (prélèvement automatique) */}
      {mandatActif && (
        <div className="pt-4 border-t space-y-3" data-testid="urssaf-sepa-block">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <CreditCard size={16} /> Prélèvement automatique Urssaf
            </h3>
            {sepa?.mandats?.length > 0 ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                <CheckCircle2 size={12} /> Actif
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                <AlertTriangle size={12} /> À configurer
              </span>
            )}
          </div>

          {sepa?.mandats?.length > 0 ? (
            <div className="space-y-1 text-sm">
              {sepa.mandats.map((m, i) => (
                <div key={m.rum || i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/40">
                  <div>
                    <div className="font-medium">{m.nomBanque || "Banque inconnue"}</div>
                    <div className="text-xs text-muted-foreground font-mono">{m.iban}</div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Titulaire : {m.titulaireCompte}
                  </div>
                </div>
              ))}
              <p className="text-xs text-muted-foreground mt-2">
                Ton mandat SEPA existant sera utilisé automatiquement pour chaque déclaration. Aucune action supplémentaire.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/50 p-4 space-y-3" data-testid="urssaf-sepa-none">
              <p className="text-sm text-amber-900">
                Aucun mandat SEPA détecté. Pour activer le <strong>paiement automatique en 1 clic</strong>,
                configure ton RIB directement sur ton espace Urssaf :
              </p>
              <ol className="text-xs text-amber-800 space-y-1 list-decimal list-inside pl-1">
                <li>Va sur ton espace <em>autoentrepreneur.urssaf.fr</em></li>
                <li>Menu <strong>« Mes coordonnées bancaires »</strong> → renseigne ton IBAN/BIC</li>
                <li>Valide le mandat SEPA (email de confirmation Urssaf)</li>
                <li>Reviens sur Biz360 et clique <strong>« Rafraîchir »</strong> ci-dessous</li>
              </ol>
              <div className="flex flex-wrap gap-2 pt-1">
                <a
                  href="https://www.autoentrepreneur.urssaf.fr/portail/accueil.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="urssaf-sepa-configure-link"
                  className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
                >
                  Configurer sur mon espace Urssaf <ExternalLink size={14} />
                </a>
                <button
                  type="button"
                  onClick={loadStatus}
                  data-testid="urssaf-sepa-refresh"
                  className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border text-sm font-medium hover:bg-muted"
                >
                  <Loader2 size={14} className={loading ? "animate-spin" : ""} /> Rafraîchir
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {connectError && !mandatActif && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2" data-testid="urssaf-connect-error">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" /> {connectError}
        </div>
      )}

      {/* Actions mandat */}
      <div className="pt-2 border-t flex flex-wrap gap-2 items-center">
        {mandatActif ? (
          <button
            type="button"
            data-testid="urssaf-revoke-btn"
            onClick={() => setRevokeOpen(true)}
            disabled={connecting}
            className="px-4 h-10 rounded-lg border border-destructive text-destructive hover:bg-destructive/10 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {connecting ? <Loader2 size={16} className="animate-spin" /> : null} Révoquer le mandat Urssaf
          </button>
        ) : (
          <button
            type="button"
            data-testid="urssaf-connect-btn"
            onClick={handleConnect}
            disabled={connecting || !profile?.siret || !profile?.date_naissance}
            className="px-4 h-10 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {connecting ? <Loader2 size={16} className="animate-spin" /> : null}
            {siretChanged ? "Reconnecter à l'Urssaf" : "Connecter mon compte Urssaf"}
          </button>
        )}
        <a
          href="https://autoentrepreneur.urssaf.fr"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary hover:underline inline-flex items-center gap-1"
        >
          Espace Urssaf officiel <ExternalLink size={12} />
        </a>
      </div>

      <UrssafRevokeMandatDialog open={revokeOpen} onOpenChange={setRevokeOpen} onRevoked={loadStatus} onReconnect={handleConnect} />

      {status?.env === "sandbox" && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
          🧪 Environnement Sandbox Urssaf activé — les déclarations ne sont pas transmises en production.
        </div>
      )}
    </div>
  );
};

export default UrssafSection;
